import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Info, MoreVertical } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { cn } from '../../../lib/utils';
import PercentageChange from '../PercentageChange/PercentageChange';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';

interface AnalyticsCardProps {
  title: string;
  tooltipText?: string;
  className?: string;
  style?: React.CSSProperties;
  contentClassName?: string;
  content?: React.ReactElement;
  prevValue?: number;
  currentValue?: number;
  showMenu?: boolean;
  onMenuClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  dropdown?: React.ReactElement;
  altColor?: boolean;
  isLoading?: boolean;
}

function AnalyticsCard({
  title,
  tooltipText,
  className,
  style,
  contentClassName,
  content,
  prevValue,
  currentValue,
  showMenu = false,
  onMenuClick,
  dropdown,
  altColor = false,
  isLoading
}: AnalyticsCardProps): React.ReactElement | null {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const titleRefCallback = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    const check = (): void => {
      setIsTitleTruncated(node.scrollWidth > node.clientWidth);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    []
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      timeoutId = setTimeout(() => {
        setShowSkeleton(true);
      }, 800);
    } else {
      setShowSkeleton(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading]);

  if (isLoading) {
    if (showSkeleton) {
      return <Skeleton className={cn('rounded-md', className)} style={style} />;
    }
    return <div className={className} style={style} />;
  }

  return (
    <Card
      className={cn(
        'flex flex-col justify-between gap-0 rounded-md py-0 shadow-none',
        altColor ? 'bg-brand-secondary text-white' : 'bg-white',
        className
      )}
      style={style}
    >
      <div className='flex items-center justify-between border-b px-4 py-2.5'>
        <div className='flex min-w-0 items-center gap-2 overflow-hidden'>
          {isTitleTruncated ? (
            <CustomTooltip content={title}>
              <span
                ref={titleRefCallback}
                className='min-w-0 overflow-hidden text-[0.9rem] font-medium text-ellipsis whitespace-nowrap'
              >
                {title}
              </span>
            </CustomTooltip>
          ) : (
            <span
              ref={titleRefCallback}
              className='min-w-0 overflow-hidden text-[0.9rem] font-medium text-ellipsis whitespace-nowrap'
            >
              {title}
            </span>
          )}
          {tooltipText && (
            <CustomTooltip content={tooltipText}>
              <Info
                className={cn(
                  'size-[18px] cursor-help',
                  altColor ? 'text-white/70' : 'text-muted-foreground'
                )}
              />
            </CustomTooltip>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {dropdown}
          {showMenu && (
            <Button variant='ghost' size='icon' onClick={onMenuClick}>
              <MoreVertical className='size-4' />
            </Button>
          )}
        </div>
      </div>

      {content && (
        <CardContent
          className={cn(
            'flex grow items-center justify-center px-0',
            contentClassName
          )}
        >
          {content}
        </CardContent>
      )}

      {prevValue !== undefined && currentValue !== undefined && (
        <div className='flex justify-start border-t px-4 py-2.5'>
          <PercentageChange
            prev={prevValue}
            current={currentValue}
            lightText={altColor}
          />
        </div>
      )}
    </Card>
  );
}

export default AnalyticsCard;
